import { batch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { CalculatorsConfig, ExtraFlags, GenieSettings, VitalsConfig } from '../api/pushGenie';
import { setupBarcode } from '../modules/barcode';
import { captureError } from '../pages/captureError';
import { Actions } from './actions';
import { fetchDoc } from './actions/login';
import {
    fetchAndStoreUhidPrefix,
    loadCalculatorConfig,
    loadPadDyForm,
    loadSectionNameConfig,
    loadVitalsConfig,
    setActiveOrderUpdatesCount,
    setExtraFlags,
    setGlobalSearchFlag,
    setIsDicomEnabled,
    setIsTreatmentPlanEnabled,
    setSetupSettings,
} from './actions/session';
import { GDyForm, SectionNameConfig } from './db';
import { DB_TABLES } from './dbTables';
import { getGArray, getItemFromMisc, MISC_KEYS } from './idbHandler';
import { GlobalState } from './reducers';
import { getPadElementMergedWithDefault } from './utils';
import { ActiveOrderUpdatesCount } from '../constants';
import { fetchAndStoreGrowthChartData } from '../pages/utils';
import { fetchMilestones } from '../modules/milestone';
import { fetchOpValues } from '../pages/opUtils';
import { Dicom, Ipd, PtDir, TreatmentServicesAndTreatmentPackages } from '../api/fetchMyGenie';
import { initAccessControl } from './AccessControl';
import { fetchAndStoreIpdData } from './actions/app';
import { changeVitalIdFromVToLBInVitalsConfig } from '../components/prescriptionPad/leftPanel/vitals';
import { v4 } from 'uuid';
import { getMActiveId } from './localstorage';
import { setMedRouteEnabled } from './actions/prescriptionPad';

const setupRedux = async (
    dispatch: ThunkDispatch<GlobalState, null, Actions>,
    docId: string,
    onSuccess: () => void,
    onAcessControlSuccess: () => void,
    shouldFetchDoc: boolean = true,
    myoid: string,
    filters?: { [k: string]: string },
    skipUhidConfigFetch?: boolean,
): Promise<void> => {
    const [
        settings,
        vitalsConfig,
        dyform,
        calculatorsConfig,
        extraFlags,
        sectionNameConfig,
        activeOrderUpdatesCount,
        treatmentServicesAndTreatmentPackages,
        dicom,
        ipd,
        ptDir
    ] = await Promise.all([
        getItemFromMisc<GenieSettings>(docId, 'settings'),
        getItemFromMisc<VitalsConfig>(docId, MISC_KEYS.vitalsConfig),
        getGArray<GDyForm>(docId, DB_TABLES.dyForm),
        getItemFromMisc<CalculatorsConfig>(docId, MISC_KEYS.calculatorConfig),
        getItemFromMisc<ExtraFlags>(docId, MISC_KEYS.extraFlags),
        getItemFromMisc<SectionNameConfig>(docId, MISC_KEYS.sectionNameConfig),
        getItemFromMisc<ActiveOrderUpdatesCount>(docId, MISC_KEYS.activeOrderUpdatesCount),
        getItemFromMisc<TreatmentServicesAndTreatmentPackages>(
            myoid || docId,
            MISC_KEYS.treatmentServicesAndTreatmentPackages,
        ),
        getItemFromMisc<Dicom>(myoid || docId, MISC_KEYS.dicom),
        getItemFromMisc<Ipd>(myoid || docId, MISC_KEYS.ipd),
        getItemFromMisc<PtDir>(getMActiveId() || '', MISC_KEYS.ptDir)
    ]);

    let padElements = getPadElementMergedWithDefault(settings.padElements, dyform);

    if (filters?.['components']?.trim()) {
        const filteredComponents = new Set(filters?.['components'].split(','));
        padElements = padElements.filter((pd) => filteredComponents.has(pd.id));
    }

    batch(() => {
        dispatch(loadVitalsConfig(changeVitalIdFromVToLBInVitalsConfig(vitalsConfig)));
        dispatch(loadPadDyForm(dyform));
        dispatch(setSetupSettings({ ...settings, padElements }));
        if (shouldFetchDoc) {
            dispatch(fetchDoc(docId, onSuccess));
        }
        dispatch(loadCalculatorConfig(calculatorsConfig));
        dispatch(setExtraFlags(extraFlags));
        dispatch(loadSectionNameConfig(sectionNameConfig));
        dispatch(setActiveOrderUpdatesCount(activeOrderUpdatesCount.count || 0));
        dispatch(setIsTreatmentPlanEnabled(treatmentServicesAndTreatmentPackages?.enabled));
        dispatch(setIsDicomEnabled(dicom?.enabled));
        dispatch(setGlobalSearchFlag(!!ptDir.enableBEGlobalSearch));
        dispatch(setMedRouteEnabled(settings.medRouteEnabled));
    });

    const trackingId = v4();

    initAccessControl(trackingId)
        .then(() => {
            onAcessControlSuccess();
        })
        .catch(captureError);

    if (settings.showMilestone) {
        fetchMilestones();
    }

    if (myoid && myoid !== docId) {
        // Going foward only my settings needs to be fetched, not the doctor one
        const mySettings = await getItemFromMisc<GenieSettings>(myoid || '', 'settings');

        if (mySettings.barcodeEnabled) {
            setupBarcode();
        }
    } else if (settings.barcodeEnabled) {
        setupBarcode();
    }

    if (settings?.growthChart) {
        fetchAndStoreGrowthChartData();
    }

    if (settings?.padElements.filter((t) => t.id.startsWith('op') && t.isShown).length) {
        fetchOpValues();
    }

    if (ipd?.enabled) {
        dispatch(fetchAndStoreIpdData());
    }
    if (!skipUhidConfigFetch) dispatch(fetchAndStoreUhidPrefix());
};

export default setupRedux;
